import type { MessageKey } from "~/i18n";
import type { ActivityEvent } from "./activitiesTypes";

export type ActivityCardActionId =
  | "approve"
  | "deny"
  | "review"
  | "run_now"
  | "view"
  | "cancel"
  | "retry";

export type ActivityCardAction = {
  readonly id: ActivityCardActionId;
  readonly labelKey: MessageKey;
  readonly variant: "default" | "outline" | "ghost" | "destructive";
};

/** Primary actions surfaced on lane cards — only when the user can do something. */
export function activityCardActions(event: ActivityEvent): readonly ActivityCardAction[] {
  switch (event.status) {
    case "needs_approval":
      return [
        { id: "approve", labelKey: "settings.comms.allowance.approve", variant: "default" },
        { id: "deny", labelKey: "settings.comms.allowance.deny", variant: "outline" },
        { id: "review", labelKey: "activities.action.review", variant: "ghost" },
      ];
    case "pending":
      if (event.kind === "schedule") {
        return [
          { id: "run_now", labelKey: "activities.action.runNow", variant: "default" },
          { id: "review", labelKey: "activities.action.review", variant: "outline" },
        ];
      }
      return [{ id: "review", labelKey: "activities.action.review", variant: "outline" }];
    case "running":
      return [
        ...(event.refs.threadId
          ? [
              {
                id: "view",
                labelKey: "activities.action.view",
                variant: "outline",
              } as ActivityCardAction,
            ]
          : []),
        { id: "cancel", labelKey: "activities.action.cancel", variant: "ghost" },
      ];
    case "failed":
      return [
        { id: "retry", labelKey: "activities.action.retry", variant: "default" },
        { id: "review", labelKey: "activities.action.review", variant: "outline" },
      ];
    default:
      return [];
  }
}

/** True when the activity still needs a human response (approve, review, retry, etc.). */
export function activityNeedsUserAttention(event: ActivityEvent): boolean {
  return activityCardActions(event).some(
    (action) => action.id !== "view" && action.id !== "cancel",
  );
}
